import React, { useState, useRef, useMemo } from "react";
import { Tooltip, Modal, Button, Checkbox, Select } from "antd";
import {
  IconEdit,
  IconExclamation,
  IconSandTimer,
} from "../Layout/Icons";
import { useDispatch } from "react-redux";
import {
  antdMessageError,
  numberRound,
  relDiff,
  returnEuroString,
} from "../../helpers/Utils";
import { putRetailPrices } from "../../redux/Actions/imum_action_retail";
import { MdCheckCircleOutline, MdLocalOffer, MdOutlinePriceCheck } from "react-icons/md";
import { BsSnow } from "react-icons/bs";
const { confirm } = Modal;
import styled from "styled-components";
import { formatDateTime } from "../../utils/date";
import RangePickerComp from "../common/RangePickerComp";
import moment from "moment";

const { Option } = Select;

const priceInputContainerStyle = { width: 150 };
const currentPriceContainerStyle = { width: 150 };
const applyAllContainerStyle = { paddingBottom: 6, whiteSpace: "nowrap" };
const sectionControlStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minHeight: 34,
  width: 150,
};
const sectionContentStyle = { marginTop: 10 };
const dividerStyle = { borderTop: "1px solid #f0f0f0" };
const priceInputStyle = {
  width: "100%",
  border: "1px solid #d9d9d9",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 14,
};

const calculateDiscountPrice = (basePrice, type, amount) => {
  const amountValue = String(amount).trim().replace(",", ".");
  if (amountValue === "") return "";
  const normalizedAmount = Number(amountValue);
  if (!Number.isFinite(normalizedAmount)) return "";
  if (type === "fixed") return numberRound(normalizedAmount);

  const basePriceValue = String(basePrice).trim().replace(",", ".");
  if (basePriceValue === "") return "";
  const normalizedBasePrice = Number(basePriceValue);
  if (!Number.isFinite(normalizedBasePrice)) return "";

  const finalPrice = type === "percent_off"
    ? normalizedBasePrice * (1 - normalizedAmount / 100)
    : normalizedBasePrice - normalizedAmount;
  return numberRound(Math.max(finalPrice, 0));
};

const FreezeToggle = ({ checked, onChange, disabled }) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      width: 32, height: 18, borderRadius: 9,
      backgroundColor: checked ? "#03B0F7" : "#ccc",
      opacity: disabled ? 0.4 : 1,
      position: "relative", cursor: disabled ? "not-allowed" : "pointer", transition: "background 0.2s",
      flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 2,
      left: checked ? 16 : 2,
      width: 14, height: 14, borderRadius: "50%",
      backgroundColor: "#fff", transition: "left 0.2s",
    }} />
  </div>
);

const DiscountToggle = ({ checked, onChange, disabled }) => (
  <div
    onClick={disabled ? undefined : onChange}
    style={{
      width: 32, height: 18, borderRadius: 9,
      backgroundColor: checked ? "#03B0F7" : "#ccc",
      opacity: disabled ? 0.4 : 1,
      position: "relative", cursor: disabled ? "not-allowed" : "pointer", transition: "background 0.2s",
      flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 2,
      left: checked ? 16 : 2,
      width: 14, height: 14, borderRadius: "50%",
      backgroundColor: "#fff", transition: "left 0.2s",
    }} />
  </div>
);

// Period is picked by date only: it starts at the beginning of the first day and ends at 23:59:59 of the last.
const periodStart = (date) => (date ? moment(date).startOf("day").toISOString() : null);
const periodEnd = (date) => (date ? moment(date).endOf("day").millisecond(0).toISOString() : null);

const RetailNewPriceChange = ({
  store,
  storeColor,
  isFirst,
  isLast,
  value,
  priceId,
  newPrice = null,
  newPriceId = null,
  freezeRecordPrice = null,
  freezeRecordId = null,
  freezeRecordFrom = null,
  freezeRecordTo = null,
  discountRecordPrice = null,
  discountRecordId = null, 
  discountRecordFrom = null,
  discountRecordTo = null,
  discountRecordType = "fixed",
  discountRecordAmount = null,
  departments = [],
  prices = [],
  parentId,
  exportStatus: exportStatusProp,
  exportDate,
  retailProductRule,
  freeze: freezeProp = false,
  freezePrice = null,
  freezeFrom = null,
  freezeTo = null,
  freezeCurrent = false,
  discount: discountProp = false,
  discountPrice = null,
  discountFrom = null,
  discountTo = null,
  discountCurrent = false,
  oldPrice = null,
  lastHistoryPrice = null,
}) => {
  const inputRef = useRef();
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [newInputValue, setNewInputValue] = useState("");
  const [freezeInputValue, setFreezeInputValue] = useState("");
  const [freeze, setFreeze] = useState(!!freezeProp);
  const [freezeRange, setFreezeRange] = useState(null);
  const [discount, setDiscount] = useState(!!discountProp);
  const [discountInputValue, setDiscountInputValue] = useState("");
  const [discountRange, setDiscountRange] = useState(null);
  const [applyNewToAllDepartments, setApplyNewToAllDepartments] = useState(false);
  const [applyFreezeToAllDepartments, setApplyFreezeToAllDepartments] = useState(false);
  const [applyDiscountToAllDepartments, setApplyDiscountToAllDepartments] = useState(false);
  const [discountType, setDiscountType] = useState("fixed");
  const [discountAmountValue, setDiscountAmountValue] = useState("");

  const activePrice = value;
  // The regular/new line must reflect only the explicit `new` record.
  // If it does not exist, show "-" instead of falling back to freeze/discount.
  const baseNewPrice = newPrice;
  const currentPriceType = discountCurrent
    ? "discount"
    : freezeCurrent
      ? "freeze"
      : "regular";

  const buildPriceState = ({ enabled, current, from, to }) => ({
    enabled,
    current,
    hasRange: !!(from && to),
    blurred: enabled && !!(from && to) && !current,
  });

  const freezeState = buildPriceState({
    enabled: freezeProp,
    current: freezeCurrent,
    from: freezeFrom,
    to: freezeTo,
  });
  const discountState = buildPriceState({
    enabled: discountProp,
    current: discountCurrent,
    from: discountFrom,
    to: discountTo,
  });
  const revertPrice = lastHistoryPrice ?? oldPrice;

  const priceLineStyle = ({ current, blurred, enabled }) => ({
    color: current ? "#212121" : enabled ? "#666" : "#999",
    fontSize: current ? "0.88rem" : "0.75rem",
    fontWeight: current ? 600 : 400,
    opacity: enabled ? 1 : 0.72,
    filter: blurred ? "blur(0.45px)" : "none",
    transition: "filter 0.2s, opacity 0.2s",
  });

  const getPeriodTooltip = (type, from, to) => {
    if (!from || !to) return null;

    return (
      <div>
        <div>{type === "discount" ? "Discount period" : "Freeze period"}</div>
        <div>{formatDateTime(from)}</div>
        <div>→ {formatDateTime(to)}</div>
      </div>
    );
  };

  const renderPeriodIcon = (type) => {
    if (type !== "discount" && type !== "freeze") return null;

    const icon = type === "discount"
      ? <MdLocalOffer size={10} color="#FA8C16" />
      : <BsSnow size={10} color="#03B0F7" />;

    return <span style={{ marginRight: 4, cursor: "default", display: "inline-flex" }}>{icon}</span>;
  };

  const priceRows = [
    discountState.enabled && discountRecordPrice !== null && {
      key: "discount",
      label: "Discount",
      value: discountRecordPrice,
      current: currentPriceType === "discount",
      enabled: discountState.enabled,
      blurred: discountState.blurred,
      from: discountFrom,
      to: discountTo,
      icon: renderPeriodIcon("discount"),
      tooltip: getPeriodTooltip("discount", discountFrom, discountTo),
    },
    freezeState.enabled && freezeRecordPrice !== null && {
      key: "freeze",
      label: "Freeze",
      value: freezeRecordPrice,
      current: currentPriceType === "freeze",
      enabled: freezeState.enabled,
      blurred: freezeState.blurred,
      from: freezeFrom,
      to: freezeTo,
      icon: renderPeriodIcon("freeze"),
      tooltip: getPeriodTooltip("freeze", freezeFrom, freezeTo),
    },
    {
      key: "regular",
      label: "Regular",
      value: baseNewPrice,
      current: currentPriceType === "regular",
      enabled: true,
      blurred: false,
      from: null,
      to: null,
      icon: null,
      tooltip: null,
    },
  ].filter(Boolean);

  const allDepartments = useMemo(() => {
    const sourceDepartments = departments?.length ? departments : [store];
    return [...new Set(sourceDepartments.filter(Boolean))];
  }, [departments, store]);

  const hasMultipleDepartments = allDepartments.length > 1;

  const findPriceRecord = (priceType, department) => {
    const found = prices?.find(
      (price) => price.price_type === priceType && price.department === department
    );
    if (found) return found;
    if (department !== store) return null;

    if (priceType === "new" && newPriceId) return { id: newPriceId };
    if (priceType === "freeze" && freezeRecordId) return { id: freezeRecordId };
    if (priceType === "discount" && discountRecordId) return { id: discountRecordId };
    return null;
  };

  const targetDepartmentsFor = (applyToAll) => applyToAll ? allDepartments : [store];

  const renderApplyAll = (checked, onChange) => (
    <Checkbox
      checked={checked}
      disabled={!hasMultipleDepartments}
      onChange={(e) => onChange(e.target.checked)}
      style={{ fontSize: 12, color: "#666" }}
    >
      Apply to all departments
    </Checkbox>
  );

  const openModal = () => {
    setNewInputValue(numberRound(baseNewPrice) || "");
    setApplyNewToAllDepartments(false);
    setFreezeInputValue(numberRound(freezeRecordPrice ?? freezePrice) ?? "");
    setFreeze(!!freezeProp);
    setApplyFreezeToAllDepartments(false);
    setFreezeRange(
      freezeRecordFrom && freezeRecordTo
        ? [moment(freezeRecordFrom), moment(freezeRecordTo)]
        : null
    );
    setDiscountInputValue(numberRound(discountRecordPrice ?? discountPrice) ?? "");
    setDiscount(!!discountProp);
    setApplyDiscountToAllDepartments(false);
    setDiscountType(discountRecordType || "fixed");
    setDiscountAmountValue(numberRound(discountRecordAmount ?? discountRecordPrice ?? discountPrice) ?? "");
    setDiscountRange(
      discountRecordFrom && discountRecordTo
        ? [moment(discountRecordFrom), moment(discountRecordTo)]
        : null
    );
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const freezeHasPeriod = !!(freezeRange?.[0] && freezeRange?.[1]);
  const discountHasPeriod = !!(discountRange?.[0] && discountRange?.[1]);

  const freezeRangeError = (() => {
    if (!freeze || !freezeHasPeriod) return null;
    const days = freezeRange[1].diff(freezeRange[0], "day");
    return days > 35 ? "Maximum allowed duration is 35 days" : null;
  })();

  const discountRangeError = (() => {
    if (!discount || !discountHasPeriod) return null;
    const days = discountRange[1].diff(discountRange[0], "day");
    return days > 35 ? "Maximum allowed duration is 35 days" : null;
  })();

  const updateDiscount = (type, amount) => {
    const normalizedNewPrice = Number(newInputValue.trim().replace(",", "."));
    const discountBasePrice = newInputValue.trim() === "" || normalizedNewPrice === 0
      ? activePrice
      : newInputValue;
    setDiscountType(type);
    setDiscountAmountValue(amount);
    setDiscountInputValue(calculateDiscountPrice(discountBasePrice, type, amount));
  };

  const submit = async (attrs) => {
    try {
      const { success } = await dispatch(putRetailPrices(parentId, attrs));
      if (success) closeModal();
      return success;
    } catch (err) {
      console.log("ERROR: ", err);
    }
  };

  const buildPrices = (newVal) => {
    const attrs = [];
    targetDepartmentsFor(applyNewToAllDepartments).forEach((department) => {
      const existing = findPriceRecord("new", department);
      const newAttrs = {
        price: newVal,
        price_type: "new",
        active: true,
        freeze_from: null,
        freeze_to: null,
      };
      if (existing?.id) newAttrs.id = existing.id;
      else newAttrs.department = department;
      attrs.push(newAttrs);
    });

    const freezeTargets = targetDepartmentsFor(applyFreezeToAllDepartments);
    if (freeze) {
      freezeTargets.forEach((department) => {
        const existing = findPriceRecord("freeze", department);
        const freezeAttrs = {
          price: freezeInputValue.trim().replace(",", "."),
          price_type: "freeze",
          active: true,
          freeze_from: periodStart(freezeRange[0]),
          freeze_to: periodEnd(freezeRange[1]),
        };
        if (existing?.id) freezeAttrs.id = existing.id;
        else freezeAttrs.department = department;
        attrs.push(freezeAttrs);
      });
    } else {
      freezeTargets.forEach((department) => {
        const existing = findPriceRecord("freeze", department);
        if (existing?.id) attrs.push({ id: existing.id, active: false });
      });
    }

    const discountTargets = targetDepartmentsFor(applyDiscountToAllDepartments);
    if (discount) {
      discountTargets.forEach((department) => {
        const existing = findPriceRecord("discount", department);
        const discountAttrs = {
          price: discountInputValue.trim().replace(",", "."),
          price_type: "discount",
          discount_type: discountType,
          discount_amount: discountAmountValue.trim().replace(",", "."),
          active: true,
          freeze_from: periodStart(discountRange[0]),
          freeze_to: periodEnd(discountRange[1]),
        };
        if (existing?.id) discountAttrs.id = existing.id;
        else discountAttrs.department = department;
        attrs.push(discountAttrs);
      });
    } else {
      discountTargets.forEach((department) => {
        const existing = findPriceRecord("discount", department);
        if (existing?.id) attrs.push({ id: existing.id, active: false });
      });
    }
    return attrs;
  };

  const handleSave = async () => {
    try {
      const trimmedNew = newInputValue.trim();
      if (trimmedNew === "") return antdMessageError("New price can't be empty");
      if (trimmedNew === "0") return antdMessageError("New price can't be zero");
      if (freeze) {
        const tf = freezeInputValue.trim();
        if (tf === "") return antdMessageError("Freeze price can't be empty");
        if (tf === "0") return antdMessageError("Freeze price can't be zero");
        if (!freezeHasPeriod) return antdMessageError("Select a freeze period first");
        if (freezeRangeError) return antdMessageError(freezeRangeError);
      }
      if (discount) {
        const td = discountInputValue.trim();
        if (td === "") return antdMessageError("Discount price can't be empty");
        if (td === "0") return antdMessageError("Discount price can't be zero");
        const discountAmount = discountAmountValue.trim();
        if (discountAmount === "") return antdMessageError("Discount amount can't be empty");
        if (discountAmount === "0") return antdMessageError("Discount amount can't be zero");
        if (discountType === "percent_off" && +discountAmount.replace(",", ".") > 100) {
          return antdMessageError("Discount percent can't be greater than 100");
        }
        if (!discountHasPeriod) return antdMessageError("Select a discount period first");
        if (discountRangeError) return antdMessageError(discountRangeError);
      }
      const new_price = trimmedNew.replace(",", ".");
      const attrs = buildPrices(new_price);

      // Warn on a large change of the base/new price
      let final_sum = relDiff(+new_price, +baseNewPrice);
      if (+new_price < +baseNewPrice) final_sum = -relDiff(+baseNewPrice, +new_price);
      if (baseNewPrice && (final_sum >= 30 || final_sum <= -30)) {
        return confirm({
          title: "Are you sure?",
          icon: <IconExclamation className="text-primary" />,
          content: `Your amount is ${final_sum >= 30 ? "higher" : "lower"
            } than before ${final_sum == "Infinity" ? "" : `${numberRound(final_sum)}%`
            }. Was: ${baseNewPrice} now: ${new_price} `,
          onOk() {
            submit(attrs);
          },
          onCancel() { },
        });
      }
      return submit(attrs);
    } catch (err) {
      console.log("ERROR: ", err);
    }
  };

  const request = async (price) => {
    const attrs = [];
    if (newPriceId) {
      attrs.push({
        id: newPriceId,
        price,
        price_type: "new",
        active: true,
        freeze_from: null,
        freeze_to: null,
      });
    }
    return submit(attrs);
  };

  const exportStatusData = useMemo(() => {
    if (exportStatusProp == null && !retailProductRule && !exportDate) return null;
    const recordType =
      exportStatusProp == null && !!retailProductRule
        ? "pending"
        : exportStatusProp
          ? "success"
          : "failed";
    return {
      recordType,
      color:
        recordType == "pending"
          ? "#9A5BD1"
          : recordType == "success"
            ? "#6BB416"
            : "#D60D00",
      export_date: exportDate,
      rule_name: retailProductRule,
    };
  }, [exportDate, exportStatusProp, retailProductRule]);

  return (
    <>
      <StyledTd
        className="text-sm position-relative"
        bgColor={storeColor}
        isFirst={isFirst}
        isLast={isLast}
        style={{ width: 100, minHeight: 54 }}
      >
        <div className="d-flex flex-column align-items-center justify-content-center" style={{ gap: 3 }}>
          {/* History price (before last change) */}
          {lastHistoryPrice !== null && (
            <div className="d-flex align-items-center justify-content-center" style={{ color: "#999", fontSize: "0.7rem" }}>
              <nobr>{returnEuroString(lastHistoryPrice)}</nobr>
            </div>
          )}
          <div className="d-flex align-items-center justify-content-center" style={{ gap: 8 }}>
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ gap: 2 }}>
              {priceRows.map((row) => (
                <Tooltip
                  key={row.key}
                  zIndex={999999}
                  placement="top"
                  title={row.tooltip}
                >
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      ...priceLineStyle(row),
                      cursor: row.tooltip ? "default" : "inherit",
                    }}
                  >
                    {row.icon}
                    <nobr>{row.value != null ? returnEuroString(row.value) : "-"}</nobr>
                  </div>
                </Tooltip>
              ))}
            </div>
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ gap: 6 }}>
              {revertPrice && revertPrice !== baseNewPrice && newPriceId && (
                <Tooltip zIndex={999999} placement="top" title="Revert to old price">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center cursor-pointer"
                    style={{ backgroundColor: "#5F5F5F", width: 12, height: 12, flexShrink: 0 }}
                    onClick={() => request(revertPrice)}
                  >
                    <MdOutlinePriceCheck size={7} color="#fff" />
                  </div>
                </Tooltip>
              )}
              <div>
                <IconEdit color="#5F5F5F" onClick={openModal} />
              </div>
            </div>
          </div>
        </div>
        {!!exportStatusData && (
          <div className="position-absolute" style={{ left: 0, top: 0 }}>
            <Tooltip
              zIndex={999999}
              placement="top"
              title={
                <div>
                  <div className="text-center font-medium">
                    {exportStatusData.recordType == "failed"
                      ? "Failed to import date"
                      : "Import date"}
                  </div>
                  <div className="text-center">
                    {exportStatusData.export_date
                      ? formatDateTime(exportStatusData.export_date)
                      : "Not imported yet"}
                  </div>
                  {!!exportStatusData.rule_name && (
                    <>
                      <div className="text-center font-medium">Rule</div>
                      <div className="text-center">{exportStatusData.rule_name}</div>
                    </>
                  )}
                </div>
              }
            >
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: exportStatusData.color,
                  borderBottomRightRadius: 5,
                  padding: "3px",
                }}
              >
                {exportStatusData.recordType == "success" ? (
                  <MdCheckCircleOutline className="text-white" style={{ width: 14, height: 14 }} />
                ) : exportStatusData.recordType == "failed" ? (
                  <IconExclamation className="text-white" />
                ) : (
                  <IconSandTimer className="text-white" style={{ width: 14, height: 14 }} />
                )}
              </div>
            </Tooltip>
          </div>
        )}
      </StyledTd>

      <Modal
        open={modalOpen}
        onCancel={closeModal}
        title={`Edit price — ${store}`}
        footer={[
          <Button key="cancel" onClick={closeModal}>Cancel</Button>,
          <Button key="save" type="primary" onClick={handleSave} disabled={!!freezeRangeError || !!discountRangeError}>Save</Button>,
        ]}
        width={600}
        zIndex={99999}
        destroyOnClose
      >
        <div className="d-flex flex-column" style={{ gap: 16 }}>
          <div className="d-flex align-items-end" style={{ gap: 12 }}>
            <div style={currentPriceContainerStyle}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 2 }}>Current price</div>
              <div style={{ fontSize: 16, fontWeight: 600, whiteSpace: "nowrap" }}>{returnEuroString(activePrice)}</div>
              {lastHistoryPrice !== null && lastHistoryPrice !== activePrice && (
                <div style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
                  Old price: <del>{returnEuroString(lastHistoryPrice)}</del>
                </div>
              )}
            </div>
            <div style={priceInputContainerStyle}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>New price</div>
              <input
                ref={inputRef}
                autoFocus
                style={priceInputStyle}
                placeholder="Enter new price"
                value={newInputValue}
                onKeyDown={(e) => {
                  if (e.key == "Enter") handleSave();
                  if (e.key == "Escape") closeModal();
                }}
                onChange={(e) => setNewInputValue(e.target.value)}
              />
            </div>
            <div style={applyAllContainerStyle}>
              {renderApplyAll(applyNewToAllDepartments, setApplyNewToAllDepartments)}
            </div>
          </div>

          <div style={dividerStyle} />

          <div>
            <div className="d-flex align-items-end" style={{ gap: 12 }}>
              <div style={sectionControlStyle}>
                <span style={{ fontSize: 13, color: "#5F5F5F" }}>Freeze</span>
                <FreezeToggle
                  checked={freeze}
                  onChange={() => setFreeze((f) => !f)}
                />
              </div>
              {freeze && (
                <>
                  <div style={priceInputContainerStyle}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Price</div>
                    <input
                      style={priceInputStyle}
                      placeholder="Enter freeze price"
                      value={freezeInputValue}
                      onKeyDown={(e) => {
                        if (e.key == "Enter") handleSave();
                        if (e.key == "Escape") closeModal();
                      }}
                      onChange={(e) => setFreezeInputValue(e.target.value)}
                    />
                  </div>
                  <div style={applyAllContainerStyle}>
                    {renderApplyAll(applyFreezeToAllDepartments, setApplyFreezeToAllDepartments)}
                  </div>
                </>
              )}
            </div>
            {freeze && (
              <div style={sectionContentStyle}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Period</div>
                <RangePickerComp
                  style={{ width: "100%" }}
                  placeholder={["Start date", "End date"]}
                  disabledDate={() => false}
                  required={true}
                  value={freezeRange}
                  onChange={(val) => setFreezeRange(val)}
                  getPopupContainer={(trigger) => trigger.closest(".ant-modal-content") || document.body}
                />
                {freezeRangeError && (
                  <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{freezeRangeError}</div>
                )}
                {!freezeHasPeriod && (
                  <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>Select a period</div>
                )}
              </div>
            )}
          </div>

          <div style={dividerStyle} />

          <div>
            <div className="d-flex align-items-end" style={{ gap: 12 }}>
              <div style={sectionControlStyle}>
                <span style={{ fontSize: 13, color: "#5F5F5F" }}>Discount</span>
                <DiscountToggle
                  checked={discount}
                  onChange={() => setDiscount((d) => !d)}
                />
              </div>
              {discount && (
                <>
                  <div style={priceInputContainerStyle}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Final price</div>
                    <input
                      style={priceInputStyle}
                      placeholder="Enter discount price"
                      value={discountInputValue}
                        readOnly
                    />
                  </div>
                  <div style={applyAllContainerStyle}>
                    {renderApplyAll(applyDiscountToAllDepartments, setApplyDiscountToAllDepartments)}
                  </div>
                </>
              )}
            </div>
            {discount && (
              <>
                <div style={sectionContentStyle}>
                  <div className="d-flex align-items-end" style={{ gap: 12 }}>
                    <div style={{ width: 200 }}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Discount type</div>
                      <Select
                        value={discountType}
                        onChange={(type) => updateDiscount(type, discountAmountValue)}
                        style={{ width: "100%" }}
                        getPopupContainer={(trigger) => trigger.closest(".ant-modal-content") || document.body}
                      >
                        <Option value="fixed">Fixed</Option>
                        <Option value="amount_off">Amount off</Option>
                        <Option value="percent_off">Percent off</Option>
                      </Select>
                    </div>
                    <div style={priceInputContainerStyle}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Discount amount</div>
                      <input
                        style={priceInputStyle}
                        placeholder="Enter discount amount"
                        value={discountAmountValue}
                        onKeyDown={(e) => {
                          if (e.key == "Enter") handleSave();
                          if (e.key == "Escape") closeModal();
                        }}
                        onChange={(e) => updateDiscount(discountType, e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div style={sectionContentStyle}>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Period</div>
                  <RangePickerComp
                    style={{ width: "100%" }}
                    placeholder={["Start date", "End date"]}
                    disabledDate={() => false}
                    required={true}
                    value={discountRange}
                    onChange={(val) => setDiscountRange(val)}
                    getPopupContainer={(trigger) => trigger.closest(".ant-modal-content") || document.body}
                  />
                  {discountRangeError && (
                    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{discountRangeError}</div>
                  )}
                  {!discountHasPeriod && (
                    <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>Select a period</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RetailNewPriceChange;

const StyledTd = styled.td`
  min-width: 100px;
  text-align: center;
  vertical-align: middle !important;
  background-color: ${(props) => props.bgColor || "#fff"};
  border-left: ${(props) =>
    props.isFirst ? "1px solid rgb(191, 191, 191)" : "none"};
  border-right: ${(props) =>
    props.isLast ? "1px solid rgb(191, 191, 191)" : "none"};
`;
